/**
 * The on-device database.
 *
 * Everything an operator produces lands here *before* the UI advances, and only then
 * does anything try to reach the network. That inversion is the point: online and
 * offline take the same code path, so the offline path is the one that gets exercised
 * every single day rather than only in a freezer.
 *
 * The store is called `assets`, not `trucks`. "Truck" is operator-facing copy and
 * lives only in the i18n catalogs; in code a forklift is an Asset.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import type { DeliveryState } from '@tes-inspect/core/outbox';
import type { PowerType } from '@tes-inspect/core/types';

export type SubmissionKind = 'inspection' | 'problem' | 'unknown_tag';

/**
 * A queued submission. Extends DeliveryState so the retry fields sit at the top
 * level, which is what lets IndexedDB index them directly.
 *
 * `id` is a client-generated UUID and doubles as the idempotency key: the server
 * inserts on conflict-do-nothing, so a retry after an ambiguous timeout is free.
 */
export interface OutboxRecord extends DeliveryState {
  readonly id: string;
  readonly kind: SubmissionKind;
  readonly shortCode: string;
  readonly assetId?: number;
  readonly operatorId?: number;
  readonly payload: unknown;
  readonly photoIds: readonly string[];
  /** The device's own clock when the operator finished, not when this was sent. */
  readonly occurredAtDeviceMs: number;
  /**
   * Server time minus device time, measured at capture rather than at send. This is
   * what files an inspection queued at 06:58 and drained at 10:00 under 06:58.
   */
  readonly clockOffsetMsAtCaptureMs: number;
  /** How stale that offset was. A week-old offset makes the timestamp low-confidence. */
  readonly offsetAgeMs: number;
  readonly serverId?: number;
}

export type PhotoUploadStatus = 'pending' | 'uploaded';

export interface PhotoRecord {
  readonly id: string;
  readonly outboxId: string;
  /** A real Blob. Never base64: a third larger, and it wrecks old Android heaps. */
  readonly blob: Blob;
  readonly bytes: number;
  readonly width: number;
  readonly height: number;
  /** A string rather than a boolean because IndexedDB cannot index a boolean. */
  readonly uploadStatus: PhotoUploadStatus;
}

export interface CachedAsset {
  readonly shortCode: string;
  readonly assetId: number;
  readonly companyId: number;
  readonly siteId: number;
  readonly assetNumber: string;
  readonly make: string;
  readonly model: string;
  readonly serialNo: string;
  readonly powerType: PowerType;
  readonly templateId: number;
  readonly lastHourMeter?: number;
  readonly status: 'active' | 'out_of_service' | 'retired';
  readonly syncedAtMs: number;
}

/**
 * Names only. No PIN, no hash, no checksum, ever.
 *
 * A roster of four-digit hashes on a phone is effectively plaintext: ten thousand
 * candidates per operator falls in minutes whatever the KDF costs. Offline PIN entry
 * is proved to the server at drain time instead.
 */
export interface CachedOperator {
  readonly operatorId: number;
  readonly companyId: number;
  readonly siteId?: number;
  readonly displayName: string;
  readonly active: boolean;
}

export interface CachedTemplate {
  readonly templateId: number;
  readonly version: number;
  readonly powerType: PowerType;
  readonly items: readonly unknown[];
}

export interface Receipt {
  readonly outboxId: string;
  readonly serverId: number;
  readonly serverReceivedAtMs: number;
  readonly occurredAtMs: number;
  readonly timeConfidence: 'normal' | 'low';
  readonly kind: SubmissionKind;
}

export interface InspectSchema extends DBSchema {
  outbox: {
    key: string;
    value: OutboxRecord;
    indexes: { 'by-status': string; 'by-next-attempt': number };
  };
  photos: {
    key: string;
    value: PhotoRecord;
    indexes: { 'by-outbox': string; 'by-upload-status': string };
  };
  assets: {
    key: string;
    value: CachedAsset;
    indexes: { 'by-site': number };
  };
  operators: {
    key: number;
    value: CachedOperator;
    indexes: { 'by-site': number };
  };
  templates: { key: number; value: CachedTemplate };
  settings: { key: string; value: { readonly key: string; readonly value: unknown } };
  receipts: {
    key: string;
    value: Receipt;
    indexes: { 'by-received': number };
  };
}

export type InspectDb = IDBPDatabase<InspectSchema>;

export const DATABASE_NAME = 'tes-inspect';
export const DATABASE_VERSION = 1;

export function openInspectDb(name: string = DATABASE_NAME): Promise<InspectDb> {
  return openDB<InspectSchema>(name, DATABASE_VERSION, {
    upgrade(db) {
      createOutbox(db);
      createPhotos(db);
      createBundle(db);
    },
  });
}

function createOutbox(db: IDBPDatabase<InspectSchema>): void {
  const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
  outbox.createIndex('by-status', 'status');
  outbox.createIndex('by-next-attempt', 'nextAttemptAtMs');

  const receipts = db.createObjectStore('receipts', { keyPath: 'outboxId' });
  receipts.createIndex('by-received', 'serverReceivedAtMs');
}

function createPhotos(db: IDBPDatabase<InspectSchema>): void {
  const photos = db.createObjectStore('photos', { keyPath: 'id' });
  photos.createIndex('by-outbox', 'outboxId');
  photos.createIndex('by-upload-status', 'uploadStatus');
}

function createBundle(db: IDBPDatabase<InspectSchema>): void {
  const assets = db.createObjectStore('assets', { keyPath: 'shortCode' });
  assets.createIndex('by-site', 'siteId');

  const operators = db.createObjectStore('operators', { keyPath: 'operatorId' });
  operators.createIndex('by-site', 'siteId');

  db.createObjectStore('templates', { keyPath: 'templateId' });
  db.createObjectStore('settings', { keyPath: 'key' });
}
